import { useCallback, useMemo } from 'react';
import { css } from '@emotion/react';
import {
  NoShowRateByType,
  RoiType,
} from '@weave/schema-gen-ts/dist/schemas/analytics-reporting/analytics_gateway/analytics_gateway.pb';
import { AnalyticsHooks, InfoTipPopover, OverviewInsights } from '@frontend/analytics-shared';
import { AreaChartData } from '@frontend/charts';
import { AppointmentReminders as AppointmentRemindersGraphic } from '@frontend/empty-states#svg';
import { useTranslation } from '@frontend/i18n';
import { breakpoints } from '@frontend/responsiveness';
import { useSettingsNavigate } from '@frontend/settings-routing';
import { formatters } from '@frontend/shared';
import { useNavigate } from '@frontend/weave-router';
import { theme } from '@frontend/theme';
import { Text, TextLink } from '@frontend/design-system';
import { useARRoiShallowStore } from '../../appointment-reminders/hooks/use-ar-roi-store';
import { useFetchARROIOOverview } from '../../appointment-reminders/hooks/use-fetch-ar-roi-overview';
import { BenchmarkComparisonText, type ChangeProps } from '../components/benchmark-comparison-text';
import { nsrTimePeriods } from '../components/filters';
import { getTimelineValuePair, useFetchNSRROITimeline } from '../hooks/use-fetch-nsr-roi-timeline';
import { useNSRRoiShallowStore, type ROIFilters } from '../hooks/use-nsr-roi-store';
import { NSRRoiTimelineChartBase } from './nsr-roi-timeline-chart-base';
import { CommonChartProps } from './types';

interface Props extends CommonChartProps {
  filters?: ROIFilters;
  isOnDashboard?: boolean;
}

const AppointmentReminders = ({ diffFromLastPeriod = 0 }: ChangeProps) => {
  const { t } = useTranslation('analytics');
  const navigate = useNavigate();
  const { navigate: settingsNavigate } = useSettingsNavigate();
  const isPositiveImpact = !diffFromLastPeriod || diffFromLastPeriod < 0;

  const { filters } = useNSRRoiShallowStore('filters');
  const { setDoNotOverrideFiltersOnLoad: setDoNotOverrideARFiltersOnLoad, setFilters: setARFilters } =
    useARRoiShallowStore('setDoNotOverrideFiltersOnLoad', 'setFilters');

  const handleNavigate = useCallback(() => {
    if (!filters) {
      return;
    }

    if (isPositiveImpact) {
      setDoNotOverrideARFiltersOnLoad(true);
      setARFilters(filters);
      navigate({
        to: '/analytics/roi/appointment-reminders',
      });
    } else {
      settingsNavigate({ to: '/messaging/auto-messages' });
    }
  }, [isPositiveImpact, filters]);

  return (
    <div css={styles.apptsWrapper}>
      <AppointmentRemindersGraphic className='graphic' />
      <div className='content'>
        <Text className='message'>
          {isPositiveImpact
            ? t('Explore how appointment reminders are reducing your no-show rate.')
            : t('Utilize appointment reminders to help lower your no-show rate.')}
        </Text>
        <TextLink onClick={handleNavigate} weight='bold'>
          {isPositiveImpact ? t('Discover the Impact') : t('Optimize Your Reminders')}
        </TextLink>
      </div>
    </div>
  );
};

export const NSRRoiBenchmarkTimelineChart = ({ filters: propsFilters, isOnDashboard, ...rest }: Props) => {
  const { t } = useTranslation('analytics');
  const { filters } = useNSRRoiShallowStore('filters');

  const { isLoading: isLoadingTimeline, rawData } = useFetchNSRROITimeline({
    additionalRequestPayload: {
      roiType: RoiType.WEAVE,
      noShowRateType: NoShowRateByType.WEAVE_AVERAGE,
    },
    filters: propsFilters || filters,
  });

  const { aggregated, isLoading: isLoadingAggregates } = useFetchARROIOOverview({
    fetchNoShowRate: false,
    filters: propsFilters || filters,
    isEnabled: isOnDashboard,
    roiType: RoiType.WEAVE,
  });

  const detailsProps = useMemo(() => {
    const differenceFromLastPeriod = Math.round(rawData?.data?.previousDateRangeDifference ?? 0);

    if (isOnDashboard || isLoadingTimeline) {
      return {};
    } else {
      return {
        afterChart: <AppointmentReminders diffFromLastPeriod={differenceFromLastPeriod} />,
        beforeChart: (
          <BenchmarkComparisonText
            diffFromLastPeriod={differenceFromLastPeriod}
            timePeriod={
              filters?.startDate && filters?.endDate
                ? AnalyticsHooks.findExistingTimePeriodKey(filters.startDate, filters.endDate, nsrTimePeriods) ??
                  undefined
                : undefined
            }
          />
        ),
      };
    }
  }, [
    filters?.endDate,
    filters?.startDate,
    isLoadingTimeline,
    isOnDashboard,
    rawData?.data?.previousDateRangeDifference,
  ]);

  const chartData = useMemo<AreaChartData>(() => {
    return {
      groups:
        rawData?.data?.dates?.map(({ key = '', params = [] }) => {
          const valuePair = getTimelineValuePair(params);

          return {
            name: key,
            values: {
              practice: valuePair['SelectedLocations'] ?? 0,
            },
          };
        }) || [],
    };
  }, [rawData]);

  return (
    <NSRRoiTimelineChartBase
      {...rest}
      {...detailsProps}
      bottomContentRenderer={
        isOnDashboard ? (
          <OverviewInsights
            title={t('No-Show Rate')}
            value={formatters.appendPercent(aggregated.noShowRatePercentage)}
          />
        ) : null
      }
      chartData={chartData}
      infoTip={
        <InfoTipPopover>{t('Track no-show performance for your location with historical trends')}</InfoTipPopover>
      }
      isLoading={isLoadingTimeline || isLoadingAggregates}
      isOnDashboard={isOnDashboard}
      title={t('No-Show Rate')}
      trackingIdBase='no-show-rate-benchmark'
    />
  );
};

const styles = {
  apptsWrapper: css`
    align-items: center;
    background-color: ${theme.colors.tangerine5};
    border-radius: ${theme.borderRadius.medium};
    border: 1px solid ${theme.colors.tangerine40};
    display: flex;
    padding: ${theme.spacing(2)};
    position: relative;

    .message {
      margin-bottom: ${theme.spacing(1)};
      max-width: calc(100% - 100px);

      @container (min-width: ${breakpoints.small.max}px) {
        max-width: calc(100% - 150px);
      }

      @container (min-width: ${breakpoints.mobile.max}px) {
        max-width: calc(100% - 200px);
      }
    }

    > div:not(.content),
    .graphic {
      height: 60px;
      max-height: 100px;
      position: absolute;
      right: ${theme.spacing(1)};

      @container (min-width: ${breakpoints.mobile.max}px) {
        height: 100%;
        right: ${theme.spacing(5)};
      }
    }

    .content {
      width: 100%;
    }
  `,
};
